import { memo, useCallback } from 'react';
import { Col, Modal, ModalBody, Spinner } from 'reactstrap';
import {
  Desktop,
  Expand,
  Mic,
  MicOff,
  Phone,
  PhoneMissed,
  Recording,
  StopRecording,
  StopScreen,
  Video,
  VideoAlt,
  VideoOff
} from '../../assets/icons/jsxIcons';
import { user } from '../../utils/api/user';
import { useRequest } from '../../utils/hooks';
import { Pressable, Text } from '../theme';
import PeerCall from '../../utils/peerCall/PeerCall';
import { useRootMemoSelector } from 'use-redux-states';
import { Avatar, Card, Row } from 'antd';
import { UserOutlined } from '@ant-design/icons';

function VideoChat({ style = {}, conversation_id }) {
  const recording = useRootMemoSelector('call.recording');

  return (
    <div
      id='video-call-container'
      style={{
        height: '250px',
        overflow: 'hidden',
        ...(recording ? { border: '4px solid #db3814' } : {}),
        ...style
      }}
      className={`chat-interaction shadow-lg`}>
      <RemoteVideo />
      <LocalVideo conversation_id={conversation_id} />
      {recording && (
        <ActionBtn style={{ background: '#fff' }}>
          <Recording fill='#fff' width={50} style={{ position: 'absolute', top: 10, left: 10 }} />
        </ActionBtn>
      )}
      <Controls />
    </div>
  );
}

const RemoteVideo = memo(() => {
  const videoOff = useRootMemoSelector('call.otherUser.videoOff');
  const screenShared = useRootMemoSelector('call.otherUser.screenShared');
  const onRemoteRef = useCallback((video) => {
    if (video) {
      PeerCall.createRemoteMedia(video);
      PeerCall.resetRemoteMediaSource();
    }
  }, []);

  return (
    <div className='chat-incoming'>
      {videoOff && !screenShared && <VideoOffCard className='border-0' />}
      <video
        ref={onRemoteRef}
        style={{ objectFit: 'cover' }}
        className='w-full h-full'
        id='remote-video'
        autoPlay
      />
    </div>
  );
});

const LocalVideo = memo(({ conversation_id, name }) => {
  const videoOff = useRootMemoSelector('call.videoOff');
  const screenShared = useRootMemoSelector('call.screenShared');

  const onRef = useCallback(
    (video) => video && PeerCall.startCall(video, conversation_id),
    [conversation_id]
  );

  return (
    <div className='chat-out rounded-xl'>
      {videoOff && !screenShared && <VideoOffCard className='' />}
      <video
        ref={onRef}
        style={{ objectFit: 'cover' }}
        id='local-video'
        className='w-full h-full'
        autoPlay
        muted
      />
    </div>
  );
});

const VideoOffCard = ({ className }) => (
  <Card className={`flex w-full h-full items-center justify-center bg-gray-900 ${className}`}>
    <Avatar icon={<UserOutlined />} />
  </Card>
);

const Controls = () => {
  const muted = useRootMemoSelector('call.muted');
  const screenShared = useRootMemoSelector('call.screenShared');
  const videoOff = useRootMemoSelector('call.videoOff');
  const recordLoading = useRootMemoSelector('call.recordLoading');
  const recording = useRootMemoSelector('call.recording');

  return (
    <Row
      id='video-call-actions'
      justify='end'
      align='middle'
      className='p-2 pr-4'
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'none'
      }}>
      <ActionBtn
        disabled={recordLoading}
        onClick={recording ? PeerCall.stopRecording : PeerCall.startRecording}>
        <StopRecording fill={recording ? '#f00' : '#fff'} width={20} height={20} />
      </ActionBtn>
      <ActionBtn onClick={PeerCall.fullScreen}>
        <Expand width={20} height={20} />
      </ActionBtn>
      <ActionBtn onClick={PeerCall.mute}>
        {muted ? <MicOff fill='#fff' width={20} height={20} /> : <Mic width={20} height={20} />}
      </ActionBtn>
      <ActionBtn onClick={PeerCall.toggleVideo}>
        {videoOff ? (
          <VideoOff fill='#fff' width={20} height={20} />
        ) : (
          <VideoAlt width={20} height={20} />
        )}
      </ActionBtn>
      <ActionBtn onClick={screenShared ? PeerCall.stopScreenSharing : PeerCall.startScreenSharing}>
        {screenShared ? <StopScreen width={20} height={20} /> : <Desktop width={20} height={20} />}
      </ActionBtn>

      <ActionBtn style={{ backgroundColor: '#dc2626 ' }} onClick={PeerCall.endCall}>
        <PhoneMissed width={20} height={20} />
      </ActionBtn>
    </Row>
  );
};

const ActionBtn = ({ children, style = {}, ...props }) => (
  <Pressable
    style={{ backgroundColor: 'rgba(0,0,0,0.4)', ...style }}
    className='p-2 px-2 rounded-circle mx-2 text-white'
    {...props}>
    {children}
  </Pressable>
);

export const IncomingVideoCallModal = memo(({ isIncoming }) => {
  const {
    state: { isLoading, data: { data } = {} }
  } = useRequest(
    {
      asyncRequest: user,
      params: [PeerCall.data?.from],
      loadOnMount: !!PeerCall?.data?.from
    },
    [PeerCall?.data?.from]
  );

  return (
    <Modal
      backdrop={false}
      toggle={PeerCall.rejectIncoming}
      contentClassName='border-0'
      className='rounded'
      style={styles.incomingModal}
      isOpen={isIncoming}>
      <ModalBody style={styles.incomingModalBody} className='flex flex-column  items-center'>
        <Row className='mt-5' justify='center' align='middle'>
          <Col>
            <div style={styles.line} />
          </Col>
          <Col>
            <Text className='text-white'>Incoming Video Call</Text>
          </Col>
          <Col>
            <div style={styles.line} />
          </Col>
        </Row>
        <div style={styles.imgCont} className='justify-center mb-5'>
          <img src={data?.profile?.picture} alt='profile' style={styles.img} />
        </div>
        {isLoading ? (
          <Spinner className='flex items-center justify-center' />
        ) : (
          <>
            <h1 className='text-white text-center mt-5'>
              {data?.profile?.name} {data?.profile?.last_name}
            </h1>
            <Text className='text-white text-center'>{data?.team}</Text>
          </>
        )}
        <Row className='mt-5' gutter={16}>
          <Col md={6}>
            <Pressable
              className='flex rounded-circle bg-danger items-center justify-center'
              style={{ ...styles.callBtn, ...styles.rejectIcon }}
              onClick={PeerCall.rejectIncoming}>
              <Phone width={40} fill='#fff' stroke={null} />
            </Pressable>
          </Col>
          <Col md={6}>
            <Pressable
              style={styles.callBtn}
              className='flex rounded-circle bg-success items-center justify-center'
              onClick={PeerCall.answerIncoming}>
              <Video width={40} />
            </Pressable>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
});

const styles = {
  callBtn: {
    width: 90,
    height: 90
  },
  rejectIcon: {
    transform: 'rotateZ(130deg)'
  },
  img: {
    width: 150,
    height: 150
  },
  imgCont: {
    width: 150,
    height: 150,
    backgroundColor: 'gray',
    borderRadius: '50%',
    overflow: 'hidden',
    marginTop: '6rem'
  },
  line: {
    backgroundColor: '#fff',
    width: 180,
    height: 1
  },
  incomingModalBody: {
    backgroundColor: '#083147',
    width: 600,
    height: 600
  },
  incomingModal: {
    width: 600
  }
};

export default memo(VideoChat);
